import React, { createContext, useCallback, useContext, useState } from 'react';
import { useAppDispatch } from '../store';
import { refreshGameForModal } from '../reducers/uiReducer';
import GamesAPI from '../api/GamesAPI';
import { EvaluationResult } from '../types/EvaluationResult';
import EvaluationLoader from './EvaluationLoader';
import { EvaluationStrength } from './evaluationStrength';

// Runs (re-)evaluation for GamesOverlayEvaluate and renders the full-cover EvaluationLoader so the
// whole overlay freezes. On success it refreshes the modal, which unmounts this and the loader.
interface EvaluationOverlayValue {
  evaluating: boolean;
  error: string;
  evaluate: (gameId: number, strength?: EvaluationStrength) => void;
  clearError: () => void;
}

const EvaluationOverlayContext = createContext<EvaluationOverlayValue>({
  evaluating: false,
  error: '',
  evaluate: () => undefined,
  clearError: () => undefined,
});

export function useEvaluationOverlay(): EvaluationOverlayValue {
  return useContext(EvaluationOverlayContext);
}

export default function EvaluationOverlayProvider(props: {
  children: React.ReactNode;
}): React.ReactNode {
  const dispatch = useAppDispatch();
  const [evaluating, setEvaluating] = useState(false);
  const [error, setError] = useState('');

  const evaluate = useCallback(
    (gameId: number, strength?: EvaluationStrength) => {
      if (evaluating) {
        return;
      }
      setError('');
      setEvaluating(true);
      GamesAPI.evaluate(gameId, strength)
        .then((data: EvaluationResult) => {
          if (data.Error == null) {
            // Reload the open game; this provider unmounts with it, so `evaluating` needn't be cleared.
            dispatch(refreshGameForModal());
          } else {
            setError(data.Error);
            setEvaluating(false);
          }
        })
        .catch((err: Error) => {
          setError(err.message);
          setEvaluating(false);
        });
    },
    [dispatch, evaluating],
  );

  const clearError = useCallback(() => setError(''), []);

  return (
    <EvaluationOverlayContext.Provider value={{ evaluating, error, evaluate, clearError }}>
      {props.children}
      {evaluating ? <EvaluationLoader /> : null}
    </EvaluationOverlayContext.Provider>
  );
}
